import { STORAGE_KEYS } from '@/config/storage-keys';
import { AccountType } from '@/types';
import type { SwitchableAccount, User } from '@/types/auth';
import { landingRouteForUser } from './landingRoute';

/**
 * Account switching between accounts that share one email — typically a channel
 * partner and the end-customer account auto-provisioned when they signed their own
 * proposal.
 */

/** Minimal Storage surface, injected so this stays testable outside a browser. */
type SessionStorageLike = {
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

export type SwitchedSessionPayload = {
  accessToken: string;
  refreshToken?: string;
  user: Partial<User> & { role?: User['role']; accountType?: User['accountType'] };
};

/**
 * Split the switcher list into the two groups shown in the dropdown. An
 * account-less internal row (super admin) counts as partner-side.
 */
export const groupSwitchableAccounts = (accounts: SwitchableAccount[]) => ({
  partnerAccounts: accounts.filter((account) => account.accountType !== AccountType.END_CUSTOMER),
  customerAccounts: accounts.filter((account) => account.accountType === AccountType.END_CUSTOMER),
});

/**
 * Establish the switched-into session in local storage and report where to land.
 *
 * Clears any impersonation from the previous session, which must not survive (same
 * reasoning as AuthContext.authUser). Callers should follow this with a full page
 * load so no react-query cache from the old account survives either.
 *
 * `STORAGE_KEYS.QUOTE` is also blanked purely to mirror the two pre-existing session
 * swaps (AccountDetails' temporary-access path, CommandCenter/TemporaryAccess). Be
 * aware it is a LEGACY key that nothing reads: the real quote drafts live under
 * `cached-quotes` (account-keyed, so switch-safe) and `cached-cc-quote` (NOT
 * account-keyed, so it does survive a switch). Clearing that one is a pre-existing
 * gap this module deliberately does not change.
 */
export const applySwitchedSession = (
  storage: SessionStorageLike,
  payload: SwitchedSessionPayload
): string => {
  storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, payload.accessToken);

  if (payload.refreshToken) {
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, payload.refreshToken);
  } else {
    // Never leave the previous account's refresh token behind: a later 401 would
    // silently refresh back into the account the user just left.
    storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  storage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);
  storage.setItem(STORAGE_KEYS.QUOTE, '');
  storage.setItem(STORAGE_KEYS.USER, JSON.stringify(payload.user));

  return landingRouteForUser({ role: payload.user?.role, accountType: payload.user?.accountType });
};
